import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  ViewChild,
  inject,
  signal,
} from '@angular/core';
import { TranslocoModule } from '@jsverse/transloco';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import {
  FileSelectEvent,
  FileUpload,
  FileUploadModule,
} from 'primeng/fileupload';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { ImageCropperComponent, ImageCroppedEvent } from 'ngx-image-cropper';
import { NotificationService } from 'src/app/core/services/notification.service';
import { UploadConfigService } from '../../services/upload-config.service';

/**
 * Reusable logo uploader with a fixed aspect ratio and a crop step.
 * The parent owns the actual upload: listen to (logoCropped) and send the Blob.
 */
@Component({
  selector: 'app-logo-upload',
  standalone: true,
  imports: [
    CommonModule,
    TranslocoModule,
    ButtonModule,
    DialogModule,
    FileUploadModule,
    ProgressSpinnerModule,
    ImageCropperComponent,
  ],
  templateUrl: './logo-upload.component.html',
  styles: [
    `
      /* Bound the cropper so large images fit without scrolling and the dialog
         keeps a stable size. */
      image-cropper {
        max-height: 60vh;
      }
    `,
  ],
})
export class LogoUploadComponent implements OnDestroy {
  private uploadConfig = inject(UploadConfigService);
  private notificationService = inject(NotificationService);

  /** Currently persisted logo URL (from the entity). */
  @Input() currentLogoUrl?: string | null;
  /** Fixed width/height ratio (1 = square). */
  @Input() aspectRatio = 1;
  /** Displays a spinner and disables interaction while the parent uploads. */
  @Input() uploading = false;
  /** Preview shape. */
  @Input() shape: 'round' | 'square' = 'round';

  /** Emits the cropped image as a Blob, ready to be uploaded by the parent. */
  @Output() logoCropped = new EventEmitter<Blob>();

  @ViewChild('fileUpload') fileUpload?: FileUpload;

  readonly config = this.uploadConfig.LOGO;

  selectedFile = signal<File | undefined>(undefined);
  cropDialogVisible = signal(false);
  /** Optimistic local preview shown right after cropping, before the parent refreshes. */
  localPreviewUrl = signal<string | undefined>(undefined);

  private croppedBlob?: Blob;

  onFileSelect(event: FileSelectEvent): void {
    const file = event.files?.[0];
    // Allow selecting the same file again next time.
    this.fileUpload?.clear();
    if (!file) {
      return;
    }

    const validation = this.uploadConfig.validateFile(file, this.config);
    if (!validation.isValid) {
      this.notificationService.error(validation.error ?? 'Invalid file');
      return;
    }

    this.selectedFile.set(file);
    this.cropDialogVisible.set(true);
  }

  onImageCropped(event: ImageCroppedEvent): void {
    this.croppedBlob = event.blob ?? undefined;
    if (event.objectUrl) {
      this.revokeLocalPreview();
      this.localPreviewUrl.set(event.objectUrl);
    }
  }

  confirmCrop(): void {
    if (this.croppedBlob) {
      this.logoCropped.emit(this.croppedBlob);
    }
    this.closeDialog();
  }

  cancelCrop(): void {
    this.revokeLocalPreview();
    this.localPreviewUrl.set(undefined);
    this.closeDialog();
  }

  ngOnDestroy(): void {
    this.revokeLocalPreview();
  }

  /** Release the object URL backing the optimistic preview to avoid leaks. */
  private revokeLocalPreview(): void {
    const url = this.localPreviewUrl();
    if (url) {
      URL.revokeObjectURL(url);
    }
  }

  private closeDialog(): void {
    this.cropDialogVisible.set(false);
    this.selectedFile.set(undefined);
    this.croppedBlob = undefined;
  }
}
